import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  AlertOctagon,
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  Ban,
  BarChart3,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Minus,
  PackagePlus,
  PackageX,
  Percent,
  RotateCcw,
  Table2,
  Truck,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import {
  useMonthlyOperations,
  type MonthlyOperationsSummary,
} from '@/api/hooks/useDashboard';
import { Badge } from '@/components/ui/badge';
import { cn, formatDate } from '@/lib/utils';

/** Which direction of change is good news for a metric. */
type Polarity = 'up' | 'down' | 'neutral';

interface YearMonth {
  year: number;
  /** 1-12. */
  month: number;
}

const CLIENT_ROWS_COLLAPSED = 10;

function currentYearMonth(): YearMonth {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function shiftMonth({ year, month }: YearMonth, delta: number): YearMonth {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function ratio(part: number, whole: number): number | null {
  return whole > 0 ? (part / whole) * 100 : null;
}

const recoveryRate = (s: MonthlyOperationsSummary) => ratio(s.recovered, s.delivered);

/** RequisitionStatus values, in the order a requisition moves through them. */
const REQUISITION_STATUS_ORDER = [1, 2, 4, 5, 3] as const;
const REQUISITION_STATUS_NAME: Record<number, string> = {
  1: 'Requested',
  2: 'Approved',
  3: 'Rejected',
  4: 'InProgress',
  5: 'Completed',
};

interface MovementRow {
  key: string;
  label: string;
  pick: (s: MonthlyOperationsSummary) => number | null;
  polarity: Polarity;
  format?: 'percent' | 'days';
}

/**
 * TOCA's month at a glance: what went out, what came back, what broke, what was
 * lost, and who moved it. Replaces the generic asset charts (by status, by
 * class, incident trend, inventory timing), which say nothing about how a
 * pallet-rental month went.
 */
export function MonthlyOperations() {
  const { t, i18n } = useTranslation('dashboard');
  const features = useFeatureFlags();
  const [selected, setSelected] = useState<YearMonth>(currentYearMonth);
  const [dailyView, setDailyView] = useState<'chart' | 'table'>('chart');
  const [showAllClients, setShowAllClients] = useState(false);

  const { data, isLoading, isError, isFetching } = useMonthlyOperations(selected.year, selected.month);

  const now = currentYearMonth();
  const isCurrentMonth = selected.year === now.year && selected.month === now.month;
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'es-MX';
  const num = (n: number) => n.toLocaleString(locale);
  const pct = (n: number | null) =>
    n === null ? '—' : `${n.toLocaleString(locale, { maximumFractionDigits: 1 })}%`;

  // "octubre de 2026" -> "Octubre de 2026" (CSS capitalize would also give "De").
  const monthLabel = (ym: YearMonth, style: 'long' | 'short' = 'long') => {
    const label = formatDate(new Date(ym.year, ym.month - 1, 1), { day: undefined, month: style, year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };
  const previousLabel = monthLabel(shiftMonth(selected, -1), 'short');

  const chartConfig = useMemo(
    (): ChartConfig => ({
      // Same identity as the six-month trend card: deliveries blue, recoveries
      // green. Validated as a categorical pair on both surfaces.
      delivered: { label: t('monthly.series.delivered'), theme: { light: '#2a78d6', dark: '#3987e5' } },
      recovered: { label: t('monthly.series.recovered'), theme: { light: '#1baf7a', dark: '#199e70' } },
    }),
    [t]
  );

  const cur = data?.current;
  const prev = data?.previous;
  const retired = (s: MonthlyOperationsSummary) => s.palletsDestroyed + s.epcsDamaged;
  const net = cur ? cur.delivered - cur.recovered : 0;

  const clients = data?.byClient ?? [];
  const visibleClients = showAllClients ? clients : clients.slice(0, CLIENT_ROWS_COLLAPSED);
  const hasDailyActivity = (data?.daily ?? []).some((d) => d.delivered > 0 || d.recovered > 0);

  // The comparison table: every movement of the month against the month
  // before, grouped the way the operation thinks about it.
  const movementGroups: { key: string; title: string; rows: MovementRow[] }[] = [
    {
      key: 'requisitions',
      title: t('monthly.groups.requisitions'),
      rows: [
        { key: 'requisitionsCreated', label: t('monthly.rows.requisitionsCreated'), pick: (s) => s.requisitionsCreated, polarity: 'neutral' },
        { key: 'requestedPallets', label: t('monthly.rows.requestedPallets'), pick: (s) => s.requestedPallets, polarity: 'neutral' },
        { key: 'requisitionsCompleted', label: t('monthly.rows.requisitionsCompleted'), pick: (s) => s.requisitionsCompleted, polarity: 'up' },
        { key: 'fillRate', label: t('monthly.rows.fillRate'), pick: (s) => s.fillRate, polarity: 'up', format: 'percent' },
        { key: 'onTimeRate', label: t('monthly.rows.onTimeRate'), pick: (s) => s.onTimeRate, polarity: 'up', format: 'percent' },
        { key: 'avgFulfillmentDays', label: t('monthly.rows.avgFulfillmentDays'), pick: (s) => s.avgFulfillmentDays, polarity: 'down', format: 'days' },
      ],
    },
    {
      key: 'flow',
      title: t('monthly.groups.flow'),
      rows: [
        { key: 'delivered', label: t('monthly.rows.delivered'), pick: (s) => s.delivered, polarity: 'up' },
        { key: 'recovered', label: t('monthly.rows.recovered'), pick: (s) => s.recovered, polarity: 'up' },
        { key: 'recoveredFromClients', label: t('monthly.rows.recoveredFromClients'), pick: (s) => s.recoveredFromClients, polarity: 'up' },
        { key: 'recoveredAtCedis', label: t('monthly.rows.recoveredAtCedis'), pick: (s) => s.recoveredAtCedis, polarity: 'up' },
        { key: 'transferred', label: t('monthly.rows.transferred'), pick: (s) => s.transferred, polarity: 'neutral' },
      ],
    },
    {
      key: 'quality',
      title: t('monthly.groups.quality'),
      rows: [
        { key: 'damaged', label: t('monthly.rows.damaged'), pick: (s) => s.damaged, polarity: 'down' },
        { key: 'repaired', label: t('monthly.rows.repaired'), pick: (s) => s.repaired, polarity: 'up' },
        { key: 'palletsDestroyed', label: t('monthly.rows.palletsDestroyed'), pick: (s) => s.palletsDestroyed, polarity: 'down' },
        { key: 'epcsDamaged', label: t('monthly.rows.epcsDamaged'), pick: (s) => s.epcsDamaged, polarity: 'down' },
        ...(features.palletIncidents
          ? [
              { key: 'lost', label: t('monthly.rows.lost'), pick: (s: MonthlyOperationsSummary) => s.lost, polarity: 'down' as Polarity },
              { key: 'qualityReturns', label: t('monthly.rows.qualityReturns'), pick: (s: MonthlyOperationsSummary) => s.qualityReturns, polarity: 'down' as Polarity },
            ]
          : []),
      ],
    },
    {
      key: 'fleet',
      title: t('monthly.groups.fleet'),
      rows: [
        { key: 'newPallets', label: t('monthly.rows.newPallets'), pick: (s) => s.newPallets, polarity: 'neutral' },
        ...(features.palletManufacture
          ? [{ key: 'manufactured', label: t('monthly.rows.manufactured'), pick: (s: MonthlyOperationsSummary) => s.manufactured, polarity: 'neutral' as Polarity }]
          : []),
        { key: 'inventoriesClosed', label: t('monthly.rows.inventoriesClosed'), pick: (s) => s.inventoriesClosed, polarity: 'neutral' },
      ],
    },
  ];

  const statusRows = REQUISITION_STATUS_ORDER.map((status) => {
    const row = data?.requisitionsByStatus.find((r) => r.status === status);
    return { status, count: row?.count ?? 0, requested: row?.requested ?? 0, supplied: row?.supplied ?? 0 };
  });
  const statusTotal = statusRows.reduce(
    (acc, r) => ({ count: acc.count + r.count, requested: acc.requested + r.requested, supplied: acc.supplied + r.supplied }),
    { count: 0, requested: 0, supplied: 0 }
  );

  const formatValue = (v: number | null, format?: MovementRow['format']) => {
    if (v === null) return '—';
    if (format === 'percent') return pct(v);
    if (format === 'days') return t('monthly.days', { value: v.toLocaleString(locale, { maximumFractionDigits: 1 }) });
    return num(v);
  };

  const rowDelta = (row: MovementRow, c: number | null, p: number | null) => {
    if (row.format === 'percent') return pointsDelta(c, p, row.polarity, 'pp');
    if (row.format === 'days') return pointsDelta(c, p, row.polarity, 'days');
    return c !== null && p !== null ? countDelta(c, p, row.polarity) : undefined;
  };

  return (
    <section className="space-y-4" aria-labelledby="monthly-ops-title">
      {/* One filter row scopes everything below it. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 id="monthly-ops-title" className="text-xl font-semibold tracking-tight">
            {t('monthly.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('monthly.description')}</p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label={t('monthly.previousMonth')}
            onClick={() => setSelected((s) => shiftMonth(s, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex min-w-[11rem] items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            {monthLabel(selected)}
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label={t('monthly.nextMonth')}
            disabled={isCurrentMonth}
            onClick={() => setSelected((s) => shiftMonth(s, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isCurrentMonth && (
            <Button variant="ghost" size="sm" onClick={() => setSelected(currentYearMonth())}>
              {t('monthly.currentMonth')}
            </Button>
          )}
        </div>
      </div>

      {isError && !data ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('monthly.loadError')}
          </CardContent>
        </Card>
      ) : (
        // Hold the previous month at reduced opacity while the next one loads,
        // instead of flashing skeletons on every month change.
        <div className={cn('space-y-4 transition-opacity', isFetching && data && 'opacity-60')}>
          {/* Headline tiles */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MonthTile
              title={t('monthly.kpis.delivered')}
              icon={Truck}
              loading={isLoading}
              value={cur ? num(cur.delivered) : '0'}
              description={t('monthly.kpis.deliveredDesc', { count: cur?.requisitionsCompleted ?? 0 })}
              delta={cur && prev ? countDelta(cur.delivered, prev.delivered, 'up') : undefined}
              previousLabel={previousLabel}
            />
            <MonthTile
              title={t('monthly.kpis.recovered')}
              icon={RotateCcw}
              loading={isLoading}
              value={cur ? num(cur.recovered) : '0'}
              description={t('monthly.kpis.recoveredDesc', {
                clients: num(cur?.recoveredFromClients ?? 0),
                cedis: num(cur?.recoveredAtCedis ?? 0),
              })}
              delta={cur && prev ? countDelta(cur.recovered, prev.recovered, 'up') : undefined}
              previousLabel={previousLabel}
            />
            <MonthTile
              title={t('monthly.kpis.net')}
              icon={ArrowLeftRight}
              loading={isLoading}
              value={net > 0 ? `+${num(net)}` : num(net)}
              description={
                net > 0
                  ? t('monthly.kpis.netOut', { count: net, value: num(net) })
                  : net < 0
                    ? t('monthly.kpis.netIn', { count: -net, value: num(-net) })
                    : t('monthly.kpis.netEven')
              }
            />
            <MonthTile
              title={t('monthly.kpis.recoveryRate')}
              icon={Percent}
              loading={isLoading}
              value={cur ? pct(recoveryRate(cur)) : '—'}
              description={t('monthly.kpis.recoveryRateDesc')}
              delta={cur && prev ? pointsDelta(recoveryRate(cur), recoveryRate(prev), 'up') : undefined}
              previousLabel={previousLabel}
            />
            <MonthTile
              title={t('monthly.kpis.onTime')}
              icon={Clock}
              loading={isLoading}
              value={cur ? pct(cur.onTimeRate) : '—'}
              description={t('monthly.kpis.onTimeDesc', { fill: pct(cur?.fillRate ?? null) })}
              delta={cur && prev ? pointsDelta(cur.onTimeRate, prev.onTimeRate, 'up') : undefined}
              previousLabel={previousLabel}
            />
            <MonthTile
              title={t('monthly.kpis.damaged')}
              icon={AlertOctagon}
              loading={isLoading}
              value={cur ? num(cur.damaged) : '0'}
              description={t('monthly.kpis.damagedDesc', { value: num(cur?.repaired ?? 0) })}
              delta={cur && prev ? countDelta(cur.damaged, prev.damaged, 'down') : undefined}
              previousLabel={previousLabel}
            />
            <MonthTile
              title={t('monthly.kpis.retired')}
              icon={Ban}
              loading={isLoading}
              value={cur ? num(retired(cur)) : '0'}
              description={t('monthly.kpis.retiredDesc', {
                destroyed: num(cur?.palletsDestroyed ?? 0),
                epcs: num(cur?.epcsDamaged ?? 0),
              })}
              delta={cur && prev ? countDelta(retired(cur), retired(prev), 'down') : undefined}
              previousLabel={previousLabel}
            />
            {features.palletIncidents ? (
              <MonthTile
                title={t('monthly.kpis.lost')}
                icon={PackageX}
                loading={isLoading}
                value={cur ? num(cur.lost) : '0'}
                description={t('monthly.kpis.lostDesc', { value: num(cur?.qualityReturns ?? 0) })}
                delta={cur && prev ? countDelta(cur.lost, prev.lost, 'down') : undefined}
                previousLabel={previousLabel}
              />
            ) : (
              <MonthTile
                title={t('monthly.kpis.newPallets')}
                icon={PackagePlus}
                loading={isLoading}
                value={cur ? num(cur.newPallets) : '0'}
                description={t('monthly.kpis.newPalletsDesc')}
                delta={cur && prev ? countDelta(cur.newPallets, prev.newPallets, 'neutral') : undefined}
                previousLabel={previousLabel}
              />
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Daily activity — chart, or the same numbers as a table */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-2">
                <div className="space-y-1.5">
                  <CardTitle className="text-base">{t('monthly.daily.title')}</CardTitle>
                  <CardDescription>{t('monthly.daily.description')}</CardDescription>
                </div>
                <ToggleGroup
                  type="single"
                  size="sm"
                  variant="outline"
                  value={dailyView}
                  onValueChange={(v) => v && setDailyView(v as 'chart' | 'table')}
                >
                  <ToggleGroupItem value="chart" aria-label={t('monthly.daily.chartView')}>
                    <BarChart3 className="h-4 w-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="table" aria-label={t('monthly.daily.tableView')}>
                    <Table2 className="h-4 w-4" />
                  </ToggleGroupItem>
                </ToggleGroup>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : !hasDailyActivity ? (
                  <div className="flex h-[300px] items-center justify-center">
                    <p className="text-sm text-muted-foreground">{t('monthly.daily.empty')}</p>
                  </div>
                ) : dailyView === 'chart' ? (
                  <ChartContainer config={chartConfig} className="h-[300px] w-full">
                    <BarChart data={data?.daily ?? []} margin={{ top: 8, right: 8, bottom: 0, left: -16 }} barGap={2}>
                      <CartesianGrid vertical={false} className="stroke-border/50" />
                      <XAxis
                        dataKey="day"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 10 }}
                        interval="preserveStartEnd"
                        minTickGap={8}
                      />
                      <YAxis tickLine={false} axisLine={false} allowDecimals={false} tick={{ fontSize: 10 }} />
                      <ChartTooltip
                        cursor={{ className: 'fill-muted/50' }}
                        content={
                          <ChartTooltipContent
                            labelFormatter={(_, payload) => {
                              const day = payload?.[0]?.payload?.day as number | undefined;
                              return day
                                ? formatDate(new Date(selected.year, selected.month - 1, day), { weekday: 'short' })
                                : '';
                            }}
                          />
                        }
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                      <Bar dataKey="delivered" fill="var(--color-delivered)" radius={[4, 4, 0, 0]} maxBarSize={14} />
                      <Bar dataKey="recovered" fill="var(--color-recovered)" radius={[4, 4, 0, 0]} maxBarSize={14} />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="max-h-[300px] overflow-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                        <tr className="border-b">
                          <th className="px-3 py-2 text-left font-medium">{t('monthly.daily.date')}</th>
                          <th className="px-3 py-2 text-right font-medium">{t('monthly.series.delivered')}</th>
                          <th className="px-3 py-2 text-right font-medium">{t('monthly.series.recovered')}</th>
                          <th className="px-3 py-2 text-right font-medium">{t('monthly.daily.net')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.daily ?? [])
                          .filter((d) => d.delivered > 0 || d.recovered > 0)
                          .map((d) => (
                            <tr key={d.day} className="border-b last:border-0">
                              <td className="px-3 py-1.5">
                                {formatDate(new Date(selected.year, selected.month - 1, d.day), { weekday: 'short' })}
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{num(d.delivered)}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{num(d.recovered)}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                                {signed(d.delivered - d.recovered, num)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Requisitions raised this month, by where they stand today */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('monthly.requisitions.title')}</CardTitle>
                <CardDescription>{t('monthly.requisitions.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : statusTotal.count === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">{t('monthly.requisitions.empty')}</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="py-2 pr-2 text-left font-medium">{t('monthly.requisitions.status')}</th>
                        <th className="px-2 py-2 text-right font-medium">{t('monthly.requisitions.folios')}</th>
                        <th className="px-2 py-2 text-right font-medium">{t('monthly.requisitions.requested')}</th>
                        <th className="py-2 pl-2 text-right font-medium">{t('monthly.requisitions.supplied')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusRows.map((r) => (
                        <tr key={r.status} className="border-b">
                          <td className="py-2 pr-2">
                            <Badge variant="outline" className="font-normal">
                              {t(`scheduler.status.${REQUISITION_STATUS_NAME[r.status]}`, REQUISITION_STATUS_NAME[r.status])}
                            </Badge>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">{num(r.count)}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{num(r.requested)}</td>
                          <td className="py-2 pl-2 text-right tabular-nums">{num(r.supplied)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-medium">
                        <td className="py-2 pr-2">{t('monthly.requisitions.total')}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{num(statusTotal.count)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{num(statusTotal.requested)}</td>
                        <td className="py-2 pl-2 text-right tabular-nums">{num(statusTotal.supplied)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Every movement of the month against the month before */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('monthly.movements.title')}</CardTitle>
              <CardDescription>
                {t('monthly.movements.description', { month: previousLabel })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-[320px] w-full" />
              ) : (
                <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
                  {movementGroups.map((group) => (
                    <table key={group.key} className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="py-2 pr-2 text-left font-semibold text-foreground">{group.title}</th>
                          <th className="px-2 py-2 text-right font-medium">{t('monthly.movements.thisMonth')}</th>
                          <th className="px-2 py-2 text-right font-medium">{previousLabel}</th>
                          <th className="py-2 pl-2 text-right font-medium">{t('monthly.movements.change')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((row) => {
                          const c = cur ? row.pick(cur) : null;
                          const p = prev ? row.pick(prev) : null;
                          return (
                            <tr key={row.key} className="border-b last:border-0">
                              <td className="py-1.5 pr-2">{row.label}</td>
                              <td className="px-2 py-1.5 text-right font-medium tabular-nums">{formatValue(c, row.format)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{formatValue(p, row.format)}</td>
                              <td className="py-1.5 pl-2 text-right">
                                <DeltaBadge delta={rowDelta(row, c, p)} compact />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Per client */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('monthly.clients.title')}</CardTitle>
              <CardDescription>{t('monthly.clients.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-[240px] w-full" />
              ) : clients.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">{t('monthly.clients.empty')}</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="py-2 pr-2 text-left font-medium">{t('monthly.clients.client')}</th>
                          <th className="px-2 py-2 text-right font-medium">{t('monthly.columns.delivered')}</th>
                          <th className="px-2 py-2 text-right font-medium">{t('monthly.columns.returned')}</th>
                          <th className="px-2 py-2 text-right font-medium">{t('monthly.columns.recoveredAtCedis')}</th>
                          <th className="px-2 py-2 text-right font-medium">{t('monthly.columns.transferred')}</th>
                          {features.palletIncidents && (
                            <th className="px-2 py-2 text-right font-medium">{t('monthly.columns.lost')}</th>
                          )}
                          <th className="px-2 py-2 text-right font-medium">{t('monthly.columns.damaged')}</th>
                          <th className="px-2 py-2 text-right font-medium" title={t('monthly.columns.netHint')}>
                            {t('monthly.columns.net')}
                          </th>
                          <th className="py-2 pl-2 text-right font-medium">{t('monthly.columns.balance')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleClients.map((c) => (
                          <tr key={c.clientId} className="border-b last:border-0">
                            <td className="max-w-[16rem] truncate py-1.5 pr-2" title={c.clientName}>{c.clientName}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{num(c.delivered)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{num(c.returned)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{num(c.recoveredAtCedis)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{num(c.transferred)}</td>
                            {features.palletIncidents && (
                              <td className="px-2 py-1.5 text-right tabular-nums">{num(c.lost)}</td>
                            )}
                            <td className="px-2 py-1.5 text-right tabular-nums">{num(c.damaged)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{signed(c.net, num)}</td>
                            <td className="py-1.5 pl-2 text-right font-medium tabular-nums">{num(c.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {clients.length > CLIENT_ROWS_COLLAPSED && (
                    <div className="flex justify-center pt-3">
                      <Button variant="ghost" size="sm" onClick={() => setShowAllClients((v) => !v)}>
                        {showAllClients
                          ? t('monthly.clients.showLess')
                          : t('monthly.clients.showAll', { count: clients.length })}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

interface Delta {
  /** Sign of the change: 1 up, -1 down, 0 flat. */
  direction: 1 | -1 | 0;
  /** Already formatted magnitude, e.g. "12%", "+8", "3.5 pp". */
  text: string;
  /** Good / bad / neither, from the metric's polarity. */
  tone: 'good' | 'bad' | 'neutral';
}

function toneFor(direction: 1 | -1 | 0, polarity: Polarity): Delta['tone'] {
  if (direction === 0 || polarity === 'neutral') return 'neutral';
  return (direction === 1) === (polarity === 'up') ? 'good' : 'bad';
}

/** Change of a count: a percentage when the base allows it, the raw difference otherwise. */
function countDelta(current: number, previous: number, polarity: Polarity): Delta {
  const diff = current - previous;
  const direction = (Math.sign(diff) as 1 | -1 | 0);
  const text =
    diff === 0
      ? '0'
      : previous > 0
        ? `${Math.round(Math.abs(diff / previous) * 100)}%`
        : `${Math.abs(diff)}`;
  return { direction, text, tone: toneFor(direction, polarity) };
}

/** Change of a rate (percentage points) or of a duration (days). */
function pointsDelta(
  current: number | null,
  previous: number | null,
  polarity: Polarity,
  unit: 'pp' | 'days' = 'pp'
): Delta | undefined {
  if (current === null || previous === null) return undefined;
  const diff = Math.round((current - previous) * 10) / 10;
  const direction = (Math.sign(diff) as 1 | -1 | 0);
  const suffix = unit === 'pp' ? ' pp' : ' d';
  return { direction, text: `${Math.abs(diff)}${suffix}`, tone: toneFor(direction, polarity) };
}

function signed(n: number, fmt: (n: number) => string): string {
  return n > 0 ? `+${fmt(n)}` : fmt(n);
}

function DeltaBadge({ delta, compact, previousLabel }: { delta?: Delta; compact?: boolean; previousLabel?: string }) {
  const { t } = useTranslation('dashboard');
  if (!delta) return compact ? <span className="text-xs text-muted-foreground">—</span> : null;
  const Icon = delta.direction === 1 ? ArrowUpRight : delta.direction === -1 ? ArrowDownRight : Minus;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs',
        delta.tone === 'good' && 'text-green-700 dark:text-green-400',
        delta.tone === 'bad' && 'text-red-700 dark:text-red-400',
        delta.tone === 'neutral' && 'text-muted-foreground'
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="sr-only">
        {delta.direction === 1 ? t('monthly.up') : delta.direction === -1 ? t('monthly.down') : t('monthly.flat')}
      </span>
      {delta.direction === 0 ? (compact ? '0' : t('monthly.noChange')) : delta.text}
      {!compact && previousLabel && <span className="text-muted-foreground">&nbsp;{t('monthly.vs', { month: previousLabel })}</span>}
    </span>
  );
}

function MonthTile({
  title,
  icon: Icon,
  value,
  description,
  delta,
  previousLabel,
  loading,
}: {
  title: string;
  icon: React.ElementType;
  value: string;
  description?: string;
  delta?: Delta;
  previousLabel?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-1">
        {loading ? <Skeleton className="h-8 w-20" /> : <div className="text-2xl font-bold">{value}</div>}
        {!loading && delta && <DeltaBadge delta={delta} previousLabel={previousLabel} />}
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardContent>
    </Card>
  );
}
